/**
 * Welche Werkzeuge ein Knoten annehmen kann — und wie die Suche darüber filtert.
 *
 * Beides steht hier und nicht im Editor, weil beides eine Regel ist und keine Darstellung: Die
 * eine entscheidet, was in der Auswahl auftaucht, die andere, was das Suchfeld davon übriglässt.
 * Rein, damit sie sich ohne bpmn-js prüfen lassen.
 */
import { ToolType } from './ToolTypes';

/** So viel braucht die Auswahl von einem Werkzeug — mehr nicht, damit sie prüfbar bleibt. */
export interface WaehlbaresWerkzeug {
  getType: () => string;
  getName: () => string;
  getDescription: () => string;
}

/**
 * **Die Gestalt des Knotens entscheidet, was hineinpasst.**
 *
 * Ein Ablauf ist ein Unterprozess, alles andere eine Dienstaufgabe — so legt es auch das Ablegen
 * aus der Palette an. Die Gestalt im Nachhinein zu verwandeln ginge nur über `bpmnReplace` und
 * hinge an jeder Kante, die schon am Knoten klebt. Eine getrennte Liste ist deshalb die ehrlichere
 * Zusage: Was in der Auswahl steht, lässt sich auch einsetzen.
 */
export const waehlbareWerkzeuge = <T extends WaehlbaresWerkzeug>(
  werkzeuge: readonly T[],
  elementTyp: string | undefined
): T[] => {
  const istUnterprozess = elementTyp === 'bpmn:SubProcess';
  return (werkzeuge ?? []).filter(werkzeug =>
    istUnterprozess
      ? werkzeug.getType() === ToolType.AGENTIC_WORKFLOW
      : werkzeug.getType() !== ToolType.AGENTIC_WORKFLOW
  );
};

/**
 * **Teilzeichenkette in Name und Beschreibung** — dieselben Felder, die auch die Palette filtert.
 *
 * Nicht die Ähnlichkeitsbewertung von cmdk: Die ist nicht vorhersagbar, und eine Auswahl, in der
 * Einträge aus einem Grund auftauchen, den niemand sieht, ist schlimmer als eine zu strenge.
 * Die Beschreibung zählt mit, weil das gesuchte Wort oft nur dort steht — wer „Ausschreibung"
 * tippt, meint auch das Werkzeug, das sie nur beschreibt.
 */
export const passtZurSuche = (wert: string, suche: string): boolean => {
  const gesucht = String(suche ?? '')
    .trim()
    .toLowerCase();
  if (!gesucht) return true;
  return String(wert ?? '')
    .toLowerCase()
    .includes(gesucht);
};

/** Der Wert, über den gesucht wird: Name und Beschreibung, in dieser Reihenfolge. */
export const suchwert = (werkzeug: WaehlbaresWerkzeug): string =>
  `${werkzeug.getName() ?? ''} ${werkzeug.getDescription() ?? ''}`.trim();
